import { Form } from "@unform/web";
import {
  Building,
  FileText,
  Pencil,
  PlusCircle,
  Route,
  Trash,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";
import { getRegistries, handleUpdatedFields } from "~/functions";
import { Scope, useField } from "@unform/core";
import { AlertContext } from "~/App";
import FormLoading from "~/components/RegisterForm/FormLoading";
import FileInput from "~/components/RegisterForm/FileInput";
import uploadFile from "~/functions/uploadFile";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";

export const InputContext = createContext({});

function WrittenTestCheckbox({ defaultChecked = false }) {
  const { fieldName, registerField } = useField("is_written");
  const inputRef = useRef(null);
  const { setSuccessfullyUpdated } = useContext(InputContext);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: (ref) => ref.current.checked,
      setValue: (ref, value) => { ref.current.checked = value === true || value === "true"; },
      clearValue: (ref) => { ref.current.checked = false; },
    });
  }, [fieldName, registerField]);

  return (
    <label className="flex shrink-0 items-center gap-2 text-xs text-gray-700 whitespace-nowrap">
      <input ref={inputRef} type="checkbox" defaultChecked={defaultChecked === true || defaultChecked === "true"} onChange={() => setSuccessfullyUpdated(false)} />
      Is written test?
    </label>
  );
}

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    name: "",
    paceGuides: [],
    loaded: false,
  });

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [version, setVersion] = useState(1);
  const generalForm = useRef();
  const typesOptions = [
    {
      value: "Content",
      label: "Content",
    },
    {
      value: "Review",
      label: "Review",
    },
    {
      value: "Progress Test",
      label: "Progress Test",
    },
    {
      value: "First Survey",
      label: "First Survey",
    },
    {
      value: "Second Survey",
      label: "Second Survey",
    },
    {
      value: "Midterm Oral Test",
      label: "Midterm Oral Test",
    },
    {
      value: "Midterm Written Test",
      label: "Midterm Written Test",
    },
    {
      value: "Final Oral Test",
      label: "Final Oral Test",
    },
    {
      value: "Final Written Test",
      label: "Final Written Test",
    },
    {
      value: "Feedback Day",
      label: "Feedback Day",
    },
    {
      value: "Viewpoint Listening Task",
      label: "Viewpoint Listening Task",
    },
    {
      value: "Case Study",
      label: "Case Study",
    },
    {
      value: "Extra Content",
      label: "Extra Content",
    },
  ].sort((a, b) => a.label > b.label);
  const { alertBox } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const [requireds, setRequireds] = useState([]);

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit(data) {
    const paceGuides = Array.isArray(data?.paceGuides) ? data.paceGuides : [];
    const hasPercentageItems = paceGuides.some((paceGuide) =>
      paceGuide.data?.some((class_) => {
        const type = (class_.type ?? "").toUpperCase();
        return type.includes("TEST") || type.includes("VIEWPOINT");
      })
    );
    if (hasPercentageItems) {
      let totalPercentage = 0;
      for (let paceGuide of paceGuides) {
        if (paceGuide.data) {
          for (let class_ of paceGuide.data) {
            if (class_.percentage) {
              totalPercentage += parseInt(class_.percentage);
            }
          }
        }
      }
      if (id !== "new" && totalPercentage !== 200) {
        toast(`The total percentage must be 200, but it is ${totalPercentage}`, {
          autoClose: 3000,
          type: "error",
          transition: Zoom,
        });
        return;
      }
    }
    if (
      !data?.level?.id ||
      !data?.languagemode?.id ||
      !data?.days_per_week ||
      !data?.hours_per_day
    ) {
      toast("Please fill in Level, Language Mode, Days per Week and Hours per Day.", {
        autoClose: 3000,
        type: "error",
        transition: Zoom,
      });
      return;
    }
    setLoading(true);
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      setLoading(false);
      return;
    }
    let levelData = null;
    try {
      const { data: fetchedLevelData } = await api.get(`levels/${data.level.id}`);
      levelData = fetchedLevelData;
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load the selected level.", {
        autoClose: 3000,
        type: "error",
        transition: Zoom,
      });
      setLoading(false);
      return;
    }
    const restHours = levelData.total_hours % data.hours_per_day;
    if (restHours > 0) {
      toast(
        "The number of hours per day must be evenly divisible by the total hours of the level!",
        { autoClose: 3000, type: "info", transition: Zoom }
      );
      const inputRef = generalForm.current.getFieldRef("hours_per_day").current;
      inputRef.select();
      setLoading(false);
      return;
    }
    if (id === "new") {
      try {
        if (data.file_id) {
          const file = data.file_id;
          let myPromise = await uploadFile(file, "Scope and Sequence");
          Promise.all([myPromise]).then((files) => {
            data.file_id = files[0];
            create(data);
          });
          return;
        } else {
          create(data);
        }
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
        setLoading(false);
      }
    } else if (id !== "new") {
      if (data.file_id) {
        const file = data.file_id;
        let myPromise = await uploadFile(file, "Scope and Sequence");
        Promise.all([myPromise]).then((files) => {
          data.file_id = files[0];

          const updated = handleUpdatedFields(data, pageData);

          if (updated.length > 0) {
            const objUpdated = Object.fromEntries(updated);
            if (objUpdated.hours_per_day || objUpdated.days_per_week) {
              alertBox({
                title: "Attention!",
                descriptionHTML:
                  "<p>You`re going to loose this workload <strong>Pace Guide</strong>.<br/>Are you sure about this action?</p>",
                buttons: [
                  {
                    title: "Cancel",
                    class: "cancel",
                  },
                  {
                    onPress: async () => {
                      send(objUpdated);
                    },
                    title: "Ok",
                  },
                ],
              });
            } else {
              send(objUpdated);
            }
          } else {
            console.log(updated);
          }
        });
      } else {
        const updated = handleUpdatedFields(data, pageData);
        if (updated.length > 0) {
          const objUpdated = Object.fromEntries(updated);
          if (objUpdated.hours_per_day || objUpdated.days_per_week) {
            alertBox({
              title: "Attention!",
              descriptionHTML:
                "<p>You`re going to loose this workload <strong>Pace Guide</strong>.<br/>Are you sure about this action?</p>",
              buttons: [
                {
                  title: "Cancel",
                  class: "cancel",
                },
                {
                  onPress: async () => {
                    send(objUpdated);
                  },
                  title: "Ok",
                },
              ],
            });
          } else {
            send(objUpdated);
          }
        } else {
          console.log(updated);
        }
      }
    }
  }

  async function create(data) {
    const response = await api.post(`/workloads`, data);
    setOpened(response.data.id);
    setPageData({ ...pageData, ...data });
    setSuccessfullyUpdated(true);
    toast("Saved!", { autoClose: 1000 });
    handleOpened(null);
    setLoading(false);
  }

  async function send(objUpdated) {
    try {
      await api.put(`/workloads/${id}`, { ...objUpdated, version });
      setPageData({ ...pageData, ...objUpdated });
      setSuccessfullyUpdated(true);
      toast("Saved!", { autoClose: 1000 });
      handleOpened(null);
      setLoading(false);
    } catch (err) {
      toast(err.response.data.error, { type: "error", autoClose: 3000 });
      setLoading(false);
    }
  }

  async function getPaceGuides(workload, paceGuideVersion) {
    const totalClasses =
      Math.round(workload.Level.total_hours / workload.hours_per_day) || 0;
    const paceGuides = [];
    for (let classDay = 1; classDay < totalClasses + 1; classDay++) {
      paceGuides.push({ day: classDay, data: [] });
    }
    const { data: paceGuidesData } = await api.get(
      `paceguides_by_workload/${id}?version=${paceGuideVersion}`
    );
    paceGuidesData.forEach((paceGuide) => {
      paceGuides[paceGuide.day - 1]?.data.push(paceGuide);
    });
    return paceGuides;
  }

  async function changeVersion(newVersion, workload = pageData) {
    try {
      const paceGuides = await getPaceGuides(workload, newVersion);
      setVersion(newVersion);
      setPageData({ ...workload, paceGuides });
      setSuccessfullyUpdated(true);
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load this version.", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  function handleSelectVersion(newVersion) {
    if (newVersion === version) return;
    if (successfullyUpdated) {
      changeVersion(newVersion);
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "<p>You have unsaved changes in this version.<br/>Do you want to discard them?</p>",
      buttons: [
        { title: "Cancel", class: "cancel" },
        { onPress: () => changeVersion(newVersion), title: "Discard" },
      ],
    });
  }

  async function createVersion(clone) {
    try {
      const { data: workload } = await api.post(`/workloads/${id}/versions`, {
        clone,
        from_version: version,
      });
      const { data: fresh } = await api.get(`workloads/${id}`);
      toast(`Version ${workload.last_version} created!`, { autoClose: 1000 });
      await changeVersion(workload.last_version, {
        ...pageData,
        default_version: fresh.default_version,
        last_version: fresh.last_version,
      });
    } catch (err) {
      toast(err?.response?.data?.error || "Could not create the version.", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  function handleNewVersion() {
    alertBox({
      title: "New version",
      descriptionHTML: `<p>Create the new version from scratch or copying the Pace Guide of version ${version}?</p>`,
      buttons: [
        { title: "Cancel", class: "cancel" },
        { onPress: () => createVersion(false), title: "From scratch" },
        { onPress: () => createVersion(true), title: `Copy v${version}` },
      ],
    });
  }

  async function handleSetDefault() {
    try {
      await api.put(`/workloads/${id}/versions/${version}/default`);
      setPageData({ ...pageData, default_version: version });
      toast(`Version ${version} is now the default!`, { autoClose: 1000 });
    } catch (err) {
      toast(err?.response?.data?.error || "Could not set the default.", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  useEffect(() => {
    async function getPageData() {
      try {
        const { data } = await api.get(`workloads/${id}`);
        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
        const selectedVersion = data.default_version || 1;
        setVersion(selectedVersion);
        const paceGuides = await getPaceGuides(data, selectedVersion);
        setPageData({
          ...data,
          loaded: true,
          paceGuides,
          name: `${data.days_per_week.toString()} day(s) per week, ${data.hours_per_day.toString()} hour(s) per day.`,
        });
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
    if (id === "new") {
      setFormType("full");
    } else if (id) {
      getPageData();
    }
  }, []);

  function handleAddContent(day) {
    setSuccessfullyUpdated(false);
    const addedPaces = pageData.paceGuides.map((paceGuide) => {
      if (paceGuide.day === day) {
        paceGuide.data.push({ type: "", description: "" });
        return paceGuide;
      } else {
        return paceGuide;
      }
    });
    setPageData({ ...pageData, paceGuides: addedPaces });
  }

  function handleRemoveContent(day, index) {
    setSuccessfullyUpdated(false);
    const removedPace = pageData.paceGuides.map((paceGuide) => {
      if (paceGuide.day === day) {
        const newData = paceGuide.data.filter(
          (_, indexGuide) => index !== indexGuide
        );
        paceGuide.data = newData;
        return paceGuide;
      } else {
        return paceGuide;
      }
    });

    const newData = generalForm.current.getData();
    newData.paceGuides
      .filter((pace) => pace.day == day)[0]
      .data.splice(index, 1);
    generalForm.current.setData(newData);

    setPageData({ ...pageData, paceGuides: removedPace });
  }

  return (
    <>
      {/* <AlertBox /> */}
      <Preview formType={formType} fullscreen={fullscreen}>
        {id !== "new" && !pageData.name ? null : pageData ? (
          formType === "preview" ? (
            <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
              <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
                <button
                  onClick={() => setFormType("full")}
                  className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
                >
                  <Pencil size={16} color="#fff" /> Open
                </button>
                <button
                  onClick={() => handleOpened(null)}
                  className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
                >
                  <X size={16} /> Close
                </button>
                <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-row items-start justify-between gap-4">
              <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="general"
                >
                  <Building size={16} /> General
                </RegisterFormMenu>
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  disabled={id === "new"}
                  messageOnDisabled="Create the workload to have access to Pace Guide."
                  activeMenu={activeMenu}
                  name="Pace Guide"
                >
                  <Route size={16} /> Pace Guide
                </RegisterFormMenu>
              </div>
              <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
                <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                  <Form
                    ref={generalForm}
                    onSubmit={handleGeneralFormSubmit}
                    className="w-full"
                  >
                    <InputContext.Provider
                      value={{
                        id,
                        generalForm,
                        setSuccessfullyUpdated,
                        fullscreen,
                        setFullscreen,
                        successfullyUpdated,
                        handleCloseForm,
                      }}
                    >
                      {id === "new" || pageData.loaded ? (
                        <>
                          <FormHeader
                            loading={loading}
                            access={access}
                            title={pageData.name}
                            registry={registry}
                            InputContext={InputContext}
                          />

                          <InputLineGroup
                            title="GENERAL"
                            activeMenu={activeMenu === "general"}
                          >
                            <InputLine title="General Data">
                              <Input
                                type="text"
                                name="name"
                                readOnly
                                required
                                title="Name"
                                grow
                                defaultValue={pageData.name}
                                InputContext={InputContext}
                              />
                              {/* <SelectPopover
                                type="text"
                                name="level_id"
                                required
                                title="Level"
                                options={levelOptions}
                                grow
                                defaultValue={
                                  pageData.Level && {
                                    value: pageData.level_id,
                                    label:
                                      pageData.Level.Programcategory.name +
                                      " - " +
                                      pageData.Level.name +
                                      " (" +
                                      pageData.Level.total_hours.toString() +
                                      "h)",
                                  }
                                }
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                type="text"
                                name="languagemode_id"
                                required
                                title="Language Mode"
                                options={languageModeOptions}
                                grow
                                defaultValue={
                                  pageData.Languagemode
                                    ? {
                                        value: pageData.languagemode_id,
                                        label: pageData.Languagemode.name,
                                      }
                                    : null
                                }
                                InputContext={InputContext}
                              /> */}
                            </InputLine>
                            <FindGeneric
                              route="levels"
                              title="Level"
                              scope="level"
                              required
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.level_id,
                                name: pageData.Level?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <FindGeneric
                              route="languagemodes"
                              title="Language Mode"
                              scope="languagemode"
                              required
                              InputContext={InputContext}
                              defaultValue={{
                                id: pageData.languagemode_id,
                                name: pageData.Languagemode?.name,
                              }}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                            <InputLine title="Configurations">
                              <Input
                                type="text"
                                name="days_per_week"
                                workloadUpdateName
                                onlyInt
                                required
                                title="Days per Week"
                                grow
                                defaultValue={pageData.days_per_week}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="hours_per_day"
                                workloadUpdateName
                                onlyFloat
                                required
                                title="Hours per Day"
                                grow
                                defaultValue={pageData.hours_per_day}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Scope and Sequence">
                              {pageData.File ? (
                                <div className="flex flex-row justify-center items-center gap-2 mt-2">
                                  File Attached:
                                  <a
                                    href={pageData.File.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex flex-row justify-center items-center gap-2 border p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-all hover:border-primary"
                                  >
                                    <FileText size={16} color="#000" /> Scope
                                    and Sequence
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPageData({ ...pageData, File: null })
                                    }
                                    className="flex flex-row justify-center items-center gap-2 border p-2 rounded-md bg-red-500 text-white hover:bg-red-600 transition-all hover:border-primary"
                                  >
                                    <Trash size={16} color="#fff" /> Delete
                                  </button>
                                </div>
                              ) : (
                                <FileInput
                                  type="file"
                                  name="file_id"
                                  title="File"
                                  grow
                                  InputContext={InputContext}
                                />
                              )}
                            </InputLine>
                          </InputLineGroup>

                          <InputLineGroup
                            title="Pace Guide"
                            activeMenu={activeMenu === "Pace Guide"}
                          >
                            <div className="flex flex-row flex-wrap items-center gap-2 px-4 pt-2 w-full">
                              {Array.from(
                                { length: pageData.last_version || 1 },
                                (_, index) => index + 1
                              ).map((tabVersion) => (
                                <button
                                  key={tabVersion}
                                  type="button"
                                  onClick={() => handleSelectVersion(tabVersion)}
                                  className={`border rounded-md py-1 px-3 text-xs transition-all ${
                                    tabVersion === version
                                      ? "bg-mila_orange text-white border-mila_orange"
                                      : "bg-slate-100 hover:border-primary hover:text-primary"
                                  }`}
                                >
                                  v{tabVersion}
                                  {tabVersion === (pageData.default_version || 1) &&
                                    " (default)"}
                                </button>
                              ))}
                              <button
                                type="button"
                                onClick={handleNewVersion}
                                className="bg-slate-100 border py-1 px-2 text-xs flex flex-row items-center gap-1 rounded-md transition-all hover:border-primary hover:text-primary"
                              >
                                <PlusCircle size={14} /> New version
                              </button>
                              {version !== (pageData.default_version || 1) && (
                                <button
                                  type="button"
                                  onClick={handleSetDefault}
                                  className="bg-slate-100 border py-1 px-2 text-xs rounded-md transition-all hover:border-primary hover:text-primary"
                                >
                                  Set v{version} as default
                                </button>
                              )}
                            </div>
                            {pageData.paceGuides.map(
                              (paceGuide, indexGuide) => {
                                return (
                                  <Scope
                                    key={`${version}-${indexGuide}`}
                                    path={`paceGuides[${indexGuide}]`}
                                  >
                                    <h3 className="font-bold pl-4 pb-2 mt-4 border-b w-full">
                                      Class {paceGuide.day} - Details
                                    </h3>
                                    {paceGuide.data.map((classes, index) => {
                                      return (
                                        <Scope
                                          key={index}
                                          path={`data[${index}]`}
                                        >
                                          <InputLine>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveContent(
                                                  paceGuide.day,
                                                  index
                                                )
                                              }
                                            >
                                              <Trash
                                                size={14}
                                                className="mt-4"
                                              />
                                            </button>
                                            <SelectPopover
                                              type="text"
                                              name="type"
                                              required
                                              title="Type"
                                              options={typesOptions}
                                              grow
                                              defaultValue={{
                                                value: classes.type,
                                                label: classes.type,
                                              }}
                                              setReturnPopover={({ value }) => {
                                                const key = `${indexGuide}-${index}`;
                                                setRequireds([
                                                  ...requireds.filter(
                                                    (i) => i.index !== key
                                                  ),
                                                  {
                                                    index: key,
                                                    value,
                                                  },
                                                ]);
                                              }}
                                              InputContext={InputContext}
                                            />
                                            {String(requireds?.find((item) => item.index === `${indexGuide}-${index}`)?.value ?? classes.type ?? "")
                                              .toUpperCase().includes("TEST") && (
                                              <WrittenTestCheckbox defaultChecked={classes.is_written} />
                                            )}
                                            <Input
                                              type="text"
                                              name="description"
                                              required
                                              title="Description"
                                              grow
                                              defaultValue={classes.description}
                                              InputContext={InputContext}
                                            />
                                            {(() => {
                                              const key = `${indexGuide}-${index}`;
                                              const selectedType = requireds?.find((i) => i.index === key)?.value ?? classes.type;
                                              return (selectedType.toUpperCase().includes("TEST") || selectedType.toUpperCase().includes("VIEWPOINT"));
                                            })() && (
                                              <Input
                                                type="text"
                                                name="percentage"
                                                title="Percentage"
                                                grow
                                                required
                                                defaultValue={
                                                  classes.percentage
                                                }
                                                InputContext={InputContext}
                                              />
                                            )}
                                          </InputLine>
                                        </Scope>
                                      );
                                    })}
                                    <Input
                                      type="hidden"
                                      name="day"
                                      required
                                      title="Day"
                                      defaultValue={paceGuide.day}
                                      InputContext={InputContext}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleAddContent(paceGuide.day)
                                      }
                                      className="bg-slate-100 border ml-6 py-1 px-2 text-xs flex flex-row justify-center items-center gap-2 rounded-md transition-all hover:border-primary hover:text-primary"
                                    >
                                      <PlusCircle size={16} /> detail to class{" "}
                                      {paceGuide.day}
                                    </button>
                                  </Scope>
                                );
                              }
                            )}
                          </InputLineGroup>
                        </>
                      ) : (
                        <FormLoading />
                      )}
                    </InputContext.Provider>
                  </Form>
                </div>
              </div>
            </div>
          )
        ) : null}
      </Preview>
    </>
  );
}
